import './userNav.scss';

import { DropdownToggle, DropdownMenu, DropdownItem, Dropdown, NavItem, Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LinkContainer } from 'react-router-bootstrap';
import { useAuthenticatedState } from '../../../api/api-authorization/useAuthenticatedState';
import { useTranslation } from 'react-i18next';
import { useToggleState } from 'use-toggle-state';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCurrentUserProfile } from '../../../api/main/profiles/useCurrentUserProfile';
import { useSubscriptionEmulation } from '../../../globalState/subscriptions/useSubscriptionEmulation';
import { useCurrentUserOrEmulatedSubscription } from '../../../globalState/subscriptions/useCurrentUserOrEmulatedSubscription';
import { ConditionalFragment } from 'react-conditionalfragment';
import { useCurrentUserRoles } from '../../../api/account';
import { IdentityRoles } from '../../../configure/security/IdentityRoles';

/**
 * Navbar area that is for the current user. 
 */
export const UserNav = () => {
    const { isAuthenticated, user } = useAuthenticatedState({ includeUser: true });
    const [dropdownOpen, toggleDropdown] = useToggleState();
    const { data: { model: profile } } = useCurrentUserProfile();
    const { t } = useTranslation();
    //Get the current user roles, so we can check if they are an admin.
    const {
        data: {
            roles: userRoles,
        }
    } = useCurrentUserRoles();

    const subscription = useCurrentUserOrEmulatedSubscription(); // Grabs current/emulated subscription

    // Emulating being in a school
    const [currentEmulatedSubscription, emulateSubscription] = useSubscriptionEmulation();

    // which trust to open when navigating to overview
    const [trustIdToUse, setTrustIdToUse] = useState<string>();

    useEffect(() => {
        // Clear states each time we are checking to prevent values getting stuck (e.g. you stop managing)
        setTrustIdToUse(undefined);

        // Use emulated trust
        if (subscription?.trustId) {
            setTrustIdToUse(subscription.trustId);
        }
    }, [subscription, setTrustIdToUse]);

    //stop emulating
    const onStopManaging = useCallback((isTrust: boolean) => {
        // We are on the school list, so when stopping we want to keep the emulated TrustId.
        if (isTrust) {
            emulateSubscription({ schoolId: null, trustId: null });
        } else {
            emulateSubscription({ schoolId: null, trustId: trustIdToUse });
        }
    }, [emulateSubscription, trustIdToUse]);

    // Work out what to show in the circle for logged in users.
    const userInitials = useMemo(() => {
        let name = profile?.firstName?.trimStart();
        if (!name) {
            name = profile?.lastName?.trimStart();
            if (!name) {
                name = user?.name?.trimStart();
            }
        }

        if (!name) {
            return '';
        }

        return name.substring(0, 1).toLocaleUpperCase();
    }, [profile, user]);


    // If the user is not logged in, then give them the login and register options.
    if (!isAuthenticated) {
        return (
            <>
                {/*<NavItem>
                    <LinkContainer className="user-nav-register-button" to="/account/register">
                        <Button color="primary">
                            {t('userNav.register', 'Sign up for free')}
                        </Button>
                    </LinkContainer>
                </NavItem>*/}
                <NavItem>
                    <LinkContainer to="/authentication/login">
                        <Button className="user-nav-login-button" color="primary">
                            {t('userNav.signIn', 'Sign in')}
                        </Button>
                    </LinkContainer>
                </NavItem>
            </>
        );
    }

    // Render the UI
    //
    // User is logged in so show their options.
    return (
        <Dropdown direction="down" isOpen={dropdownOpen} toggle={() => toggleDropdown()} nav>
            <DropdownToggle nav>
                <div className="user-nav-circle">
                    {userInitials}
                </div>
            </DropdownToggle>
            <DropdownMenu end>
                <DropdownItem header tag="div" className="text-center"><strong>{t('userNav.account', 'Account')}</strong></DropdownItem>
                <LinkContainer to={'/profile/details'}>
                    <DropdownItem>
                        <FontAwesomeIcon icon="user" />
                        <> {t('userNav.profile', 'Profile')}</>
                    </DropdownItem>
                </LinkContainer>


                {/* Allow a user to stop managing a trust without having to navigate back to the admin screen */}
                <ConditionalFragment showIf={currentEmulatedSubscription?.trustId !== null && !!userRoles?.find(r => r === IdentityRoles.Administration)}>
                    <LinkContainer to="/administration/trusts" onClick={() => onStopManaging(true)}>
                        <DropdownItem >
                            <FontAwesomeIcon icon="stop" className="nav-icon" />
                            <> {t('userNav.stopManagingTrust', 'Stop managing trust')}</>
                        </DropdownItem>
                    </LinkContainer>
                </ConditionalFragment>

                {/* Allow a user to stop managing a school without having to navigate back to the admin screen */}
                <ConditionalFragment showIf={currentEmulatedSubscription?.schoolId !== null && !!userRoles?.find(r => r === IdentityRoles.Administration)}>
                    <LinkContainer to="/administration/schools" onClick={() => onStopManaging(false)}>
                        <DropdownItem>
                            <FontAwesomeIcon icon="stop" className="nav-icon" />
                            <> {t('userNav.stopManagingSchool', 'Stop managing school')}</>
                        </DropdownItem>
                    </LinkContainer>
                </ConditionalFragment>

                <DropdownItem divider />

                <LinkContainer to="/authentication/logout" state={{ local: true }}>
                    <DropdownItem>
                        <FontAwesomeIcon icon="lock" />
                        <> {t('userNav.signOut', 'Sign out')}</>
                    </DropdownItem>
                </LinkContainer>
            </DropdownMenu>
        </Dropdown>
    );
};
